import { RealTimeEvse, RealTimeLocation, TariffElement } from '@domain/graphql.types';

/** Minutes since midnight for "now" in an IANA timezone. */
export function minutesSinceMidnightInTZ(now: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const mm = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return hh * 60 + mm;
}

/** Hour (0–23) of "now" in an IANA timezone. */
export const currentHourInTZ = (now: Date, timeZone = 'UTC') => Math.floor(minutesSinceMidnightInTZ(now, timeZone) / 60);

function parseHHMM(s: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) throw new Error(`Invalid time "${s}" (expected HH:MM)`);
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) {
    throw new Error(`Out-of-range time "${s}"`);
  }
  return hh * 60 + mm;
}

function isTariffElementActiveNow(t: TariffElement, timeZone: string, now = new Date()): boolean {
  const r = t.restrictions ?? null;

  const hasStart = !!r?.startTime;
  const hasEnd = !!r?.endTime;
  if (!r || (!hasStart && !hasEnd)) return true;

  const startMin = r?.startTime ? parseHHMM(r.startTime) : 0;
  const endMin = r?.endTime ? parseHHMM(r.endTime) : 23 * 60 + 59;

  if (hasStart && hasEnd && startMin === endMin) return true;
  const nowMin = minutesSinceMidnightInTZ(now, timeZone);

  if (startMin <= endMin) return nowMin >= startMin && nowMin <= endMin;
  return nowMin >= startMin || nowMin <= endMin;
}

/** Average reliability score of the charging stations (0–100), or null when none has a score. */
export function avgReliabilityScore(chargingPool: RealTimeLocation['chargingPool'] | undefined | null): number | null {
  const scores = (chargingPool ?? []).map((cp) => cp?.reliabilityScore).filter((s): s is number => typeof s === 'number');
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

export type ReliabilityLevel = 'poor' | 'low' | 'fair' | 'good' | 'excellent' | 'na';

/** Level of a 0–100 reliability score, classified on the rounded score so it matches the number shown. */
export function reliabilityLevel(score: number | null): ReliabilityLevel {
  if (score === null) return 'na';
  const rounded = Math.round(score);
  if (rounded < 60) return 'poor';
  if (rounded < 80) return 'low';
  if (rounded < 90) return 'fair';
  if (rounded < 95) return 'good';
  return 'excellent';
}

export type Pricing = {
  pricing: string;
  pricingNumeric: number;
  /** unit of the base price, null when there is none */
  pricingUnit: 'kWh' | 'h' | null;
  idleFee: string;
  flatFee: string;
};

/** Current non-member (or member) tariff of the location: base price, idle and flat fees. */
export function extractPricing(chargingPool: RealTimeLocation['chargingPool'] | undefined | null, timeZone = 'UTC'): Pricing {
  const blank: Pricing = { pricing: '-', pricingNumeric: 0, pricingUnit: null, idleFee: '-', flatFee: '-' };
  if (!chargingPool || chargingPool.length === 0) return blank;

  const withPricing = chargingPool.filter((cp) => !!cp?.currentPricing);
  if (withPricing.length === 0) return blank;

  const pricingBucket = withPricing[0]?.currentPricing ?? {};
  const bucket = pricingBucket['non-member'] ?? pricingBucket.member;
  const elements: TariffElement[] = Array.isArray(bucket?.elements) ? bucket?.elements : [];
  if (elements.length === 0) return blank;

  const findElem = (predicate: (e: TariffElement) => boolean) => elements.find(predicate);

  const baseElem = findElem(
    (e) =>
      !Object.hasOwn(e.restrictions ?? {}, 'vehicleRequestingPower') &&
      isTariffElementActiveNow(e, timeZone) &&
      e.priceComponents.some((pc) => pc.type === 'ENERGY' || pc.type === 'TIME'),
  );

  let pricing = '-';
  let pricingNumeric = 0;
  let pricingUnit: Pricing['pricingUnit'] = null;

  if (baseElem) {
    const energy = baseElem.priceComponents.find((pc) => pc.type === 'ENERGY');
    const time = baseElem.priceComponents.find((pc) => pc.type === 'TIME');
    if (energy) {
      pricingNumeric = energy.price ?? 0;
      pricing = `${energy.price}/kWh`;
      pricingUnit = 'kWh';
    } else if (time) {
      pricingNumeric = time.price ?? 0;
      pricing = `${time.price}/h`;
      pricingUnit = 'h';
    }
  }

  const idleElem = findElem(
    (e) => !!e.restrictions?.vehicleRequestingPower && isTariffElementActiveNow(e, timeZone) && e.priceComponents.some((pc) => pc.type === 'TIME'),
  );
  const idlePc = idleElem?.priceComponents.find((pc) => pc.type === 'TIME');
  const idleFee = idlePc ? `${idlePc.price}/h` : '-';

  const flatElem = findElem(
    (e) =>
      !Object.hasOwn(e.restrictions ?? {}, 'vehicleRequestingPower') &&
      isTariffElementActiveNow(e, timeZone) &&
      e.priceComponents.some((pc) => pc.type === 'FLAT'),
  );
  const flatPc = flatElem?.priceComponents.find((pc) => pc.type === 'FLAT');
  const flatFee = flatPc ? String(flatPc.price) : '-';

  return { pricing, pricingNumeric, pricingUnit, idleFee, flatFee };
}

/** An EVSE (port) with the reliability score of its charging station, which the API only gives per station. */
export type RealTimePort = {
  evse: RealTimeEvse;
  reliabilityScore: number | null;
};

export const getRealTimePorts = (rtl: RealTimeLocation | null | undefined): RealTimePort[] =>
  (rtl?.chargingPool ?? []).flatMap((pool) => (pool?.evses ?? []).map((evse) => ({ evse, reliabilityScore: pool?.reliabilityScore ?? null })));

/**
 * The three port states shown to users. EVSE statuses are OCPI strings (not a GraphQL enum):
 * AVAILABLE is free, CHARGING / BLOCKED / RESERVED are in use, the others cannot be used now.
 * A missing or UNKNOWN status is told as unknown rather than claiming the port is broken.
 */
export type PortStatus = 'available' | 'charging' | 'outOfService' | 'unknown';

export const getPortStatus = (status: string | null | undefined): PortStatus => {
  switch ((status ?? '').toUpperCase()) {
    case 'AVAILABLE':
      return 'available';
    case 'CHARGING':
    case 'BLOCKED':
    case 'RESERVED':
      return 'charging';
    case '':
    case 'UNKNOWN':
      return 'unknown';
    default:
      return 'outOfService';
  }
};

/** Highest connector power of an EVSE, in kW. */
export const getEvseMaxPowerKw = (evse: RealTimeEvse): number | null => {
  const powers = (evse.connectors ?? []).map((c) => Number.parseFloat(String(c.maxPower))).filter((p) => Number.isFinite(p) && p > 0);
  return powers.length ? Math.max(...powers) : null;
};

export type AvailabilityLevel = 'available' | 'busy' | 'unavailable';

export type Availability = {
  available: number;
  total: number;
  /** green by default, orange under 30 % available, red when none is */
  level: AvailabilityLevel;
};

/** Available ports out of all the ports of the location; null when it reports no port. */
export const getAvailability = (rtl: RealTimeLocation | null | undefined): Availability | null => {
  const ports = getRealTimePorts(rtl);
  if (!ports.length) return null;

  const available = ports.filter(({ evse }) => getPortStatus(evse.status) === 'available').length;
  const total = ports.length;
  const level: AvailabilityLevel = available === 0 ? 'unavailable' : available / total < 0.3 ? 'busy' : 'available';

  return { available, total, level };
};

/**
 * Expected wait in minutes: 0 when a port is free, otherwise the shortest estimate of the charging ports.
 * null when it cannot be told.
 */
export const getWaitMinutes = (rtl: RealTimeLocation | null | undefined): number | null => {
  const evses = getRealTimePorts(rtl).map(({ evse }) => evse);
  if (!evses.length) return null;
  if (evses.some((e) => getPortStatus(e.status) === 'available')) return 0;

  const waits = evses
    .filter((e) => getPortStatus(e.status) === 'charging' && typeof e.estimatedWaitTime === 'number')
    .map((e) => e.estimatedWaitTime as number);
  if (!waits.length) return null;

  return Math.max(0, Math.round(Math.min(...waits) / 60));
};

/** Average share of each hour (0–23) the ports spend charging, from 0 to 1. */
export function buildHourlyUtilization(rtl: RealTimeLocation | null | undefined): number[] {
  const evses = getRealTimePorts(rtl).map(({ evse }) => evse);
  const count = evses.length || 1;
  const totals = Array(24).fill(0);
  evses.forEach((evse) => {
    const hourly = evse.metrics?.hourlyHistoricalUtilization as Record<string, { chargingSeconds?: number }> | undefined;
    if (!hourly) return;
    for (let h = 0; h < 24; h++) {
      totals[h] += hourly[String(h)]?.chargingSeconds || 0;
    }
  });
  return totals.map((secs) => Math.max(0, Math.min(1, secs / (count * 3600) || 0)));
}
